import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

export interface SegmentedOption<V extends string> {
  value: V;
  labelKey: string;
}

interface SegmentedProps<V extends string> {
  value: V;
  onChange: (value: V) => void;
  options: SegmentedOption<V>[];
  /** Accessible name for the radiogroup. Pass a pre-translated string. */
  ariaLabel: string;
  className?: string;
}

// Single-choice segmented control (role=radiogroup). Clones TypeSegment's look
// and adds roving-focus arrow-key navigation + Enter/Space selection so it is
// fully keyboard operable. Generic over the option value union.
export function Segmented<V extends string>({
  value,
  onChange,
  options,
  ariaLabel,
  className,
}: SegmentedProps<V>) {
  const { t } = useTranslation();
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  // When no option matches the current value (e.g. the empty-string "unanswered"
  // state YesNoToggle passes), keep the first option in the tab order so the
  // control stays keyboard-reachable.
  const noneSelected = !options.some((o) => o.value === value);

  function focusAt(index: number) {
    const n = options.length;
    const i = ((index % n) + n) % n;
    refs.current[i]?.focus();
    onChange(options[i]!.value);
  }

  function onKeyDown(e: React.KeyboardEvent, index: number) {
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault();
        focusAt(index + 1);
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault();
        focusAt(index - 1);
        break;
      case 'Home':
        e.preventDefault();
        focusAt(0);
        break;
      case 'End':
        e.preventDefault();
        focusAt(options.length - 1);
        break;
      case ' ':
      case 'Enter':
        e.preventDefault();
        onChange(options[index]!.value);
        break;
    }
  }

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={cn(
        'inline-flex flex-wrap gap-0.5 rounded-md border border-line bg-paper-3 p-[3px]',
        className,
      )}
    >
      {options.map((opt, i) => {
        const on = opt.value === value;
        return (
          <button
            key={opt.value}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            role="radio"
            aria-checked={on}
            // Roving tabindex: only the checked option (or the first, when none
            // is selected) is in the tab order.
            tabIndex={on || (noneSelected && i === 0) ? 0 : -1}
            onClick={() => onChange(opt.value)}
            onKeyDown={(e) => onKeyDown(e, i)}
            className={cn(
              'flex min-h-[44px] flex-1 items-center justify-center rounded-[7px] px-4 text-[13.5px] tracking-[-0.005em] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background',
              on
                ? 'border border-line bg-paper font-semibold text-ink shadow-sm'
                : 'border border-transparent font-medium text-muted hover:text-ink-2',
            )}
          >
            {t(opt.labelKey)}
          </button>
        );
      })}
    </div>
  );
}
